"use client";
import React from "react";

export default function Particles({
  scopeRef,                       // 👈 NEW: the element to size to
  count = 40,
  colorLight = "#9CA3AF",
  colorDark = "#79a8ffff",
  alphaLight = 0.5,
  alphaDark = 0.5,
}) {
  const canvasRef = React.useRef(null);
  const rafRef = React.useRef(null);
  const resizeObsRef = React.useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const target = scopeRef?.current || canvas.parentElement || canvas;
    let w = 0, h = 0, dpr = 1;
    let running = true;

    const hexToRgba = (hex, a = 1) => {
      let c = hex.replace("#", "");
      if (c.length === 3) c = c.split("").map((ch) => ch + ch).join("");
      const r = parseInt(c.slice(0, 2), 16);
      const g = parseInt(c.slice(2, 4), 16);
      const b = parseInt(c.slice(4, 6), 16);
      return `rgba(${r},${g},${b},${a})`;
    };

    const drawStar = (cx, cy, spikes, outerR, innerR, color) => {
      let rot = (Math.PI / 2) * 3;
      let x = cx, y = cy;
      const step = Math.PI / spikes;
      ctx.beginPath();
      ctx.moveTo(cx, cy - outerR);
      for (let i = 0; i < spikes; i++) {
        x = cx + Math.cos(rot) * outerR;
        y = cy + Math.sin(rot) * outerR;
        ctx.lineTo(x, y);
        rot += step;
        x = cx + Math.cos(rot) * innerR;
        y = cy + Math.sin(rot) * innerR;
        ctx.lineTo(x, y);
        rot += step;
      }
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    };

    const particles = Array.from({ length: count }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0005,
      vy: (Math.random() - 0.5) * 0.0005,
      r: 0.002 + Math.random() * 0.004,
    }));

    const measure = () => {
      dpr = window.devicePixelRatio || 1;
      const rect = target?.getBoundingClientRect();
      w = Math.max(1, rect?.width ?? canvas.clientWidth ?? 1);
      h = Math.max(1, rect?.height ?? canvas.clientHeight ?? 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const tick = () => {
      if (!running) return;
      const isDark = document.documentElement.classList.contains("dark");
      const color = isDark ? hexToRgba(colorDark, alphaDark)
                           : hexToRgba(colorLight, alphaLight);

      ctx.clearRect(0, 0, w, h);
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > 1) p.vx *= -1;
        if (p.y < 0 || p.y > 1) p.vy *= -1;

        const size = p.r * Math.min(w, h) * 2;
        drawStar(p.x * w, p.y * h, 5, size, size / 2, color);
      });

      rafRef.current = requestAnimationFrame(tick);
    };

    // Initial layout + observers
    measure();

    if ("ResizeObserver" in window && target) {
      resizeObsRef.current = new ResizeObserver(measure);
      resizeObsRef.current.observe(target);
    } else {
      window.addEventListener("resize", measure);
    }

    // Pause when off-screen
    let io;
    if ("IntersectionObserver" in window && target) {
      io = new IntersectionObserver(
        ([entry]) => {
          const nowVisible = entry.isIntersecting;
          if (nowVisible && !running) {
            running = true;
            rafRef.current = requestAnimationFrame(tick);
          } else if (!nowVisible && running) {
            running = false;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
          }
        },
        { root: null, threshold: 0.01 }
      );
      io.observe(target);
    }

    // Start animating
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (resizeObsRef.current && target) resizeObsRef.current.unobserve(target);
      window.removeEventListener("resize", measure);
      io?.disconnect();
    };
  }, [scopeRef, count, colorLight, colorDark, alphaLight, alphaDark]);

  return (
    <div className="absolute inset-0 z-0 pointer-events-none">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
